/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import moment from 'moment';
import { setupOptions } from '../helper/setup-options.helper';
import { CoreErrorKey } from '../key/core-error.key';
import { CoreFormatKey } from '../key/core-format.key';
import { CoreFormatterKey } from '../key/core-formatter.key';
import { ApplicationError } from '../model/application-error.model';
import { Formatter, FormatterProvider } from '../provider/formatter.provider';

export interface DateFormatterOptions {
  format?: string;
  parseTo?: 'iso' | 'number' | 'date' | 'utc-iso' | 'utc-string' | 'utc-date';
  nullFormat?: string;
  offsetMode?: 'local' | 'utc' | 'zone'; // local ... local time, utc ... utc time, zone ... zone time;
  strict?: boolean;
}

export class DateFormatter implements Formatter {
  protected readonly options: DateFormatterOptions;

  constructor(options?: DateFormatterOptions, private readonly key = CoreFormatterKey.DATE) {
    this.options = DateFormatter.setupOptions(options, key);
  }

  format(value: number | Date | string): string {
    if (value === undefined) {
      return '';
    }
    if (value === null || value === '' || value === 0) {
      return this.options.nullFormat;
    }
    let momentValue: moment.Moment;
    switch (this.options.offsetMode) {
      case 'local':
        momentValue = moment(value);
        break;
      case 'utc':
        momentValue = moment.utc(value);
        break;
      case 'zone':
        momentValue = moment.parseZone(value);
        break;
      default:
        throw new ApplicationError({
          key: CoreErrorKey.FORMAT_ERROR,
          arguments: { formatterKey: this.key, options: this.options, value },
          message: `Format value error: Offset mode '${this.options.offsetMode}' invalid`
        });
    }
    if (!momentValue.isValid()) {
      throw new ApplicationError({
        key: CoreErrorKey.FORMAT_ERROR,
        arguments: { formatterKey: this.key, options: this.options, value },
        message: `Format value error: Value '${value}' invalid (${this.key})`
      });
    }
    return momentValue.format(this.options.format);
  }

  parse(value: string): string | number | Date {
    if (value === undefined) {
      return undefined;
    }
    if (!value || value === this.options.nullFormat) {
      return null;
    }
    let momentValue: moment.Moment;
    switch (this.options.offsetMode) {
      case 'local':
        momentValue = moment(value, this.options.format, this.options.strict);
        break;
      case 'utc':
        momentValue = moment.utc(value, this.options.format, this.options.strict);
        break;
      case 'zone':
        momentValue = moment.parseZone(value, this.options.format, this.options.strict);
        break;
      default:
        throw new ApplicationError({
          key: CoreErrorKey.PARSE_ERROR,
          arguments: { formatterKey: this.key, options: this.options, value },
          message: `Parse value error: Offset mode '${this.options.offsetMode}' invalid`
        });
    }
    if (!momentValue.isValid()) {
      throw new ApplicationError({
        key: CoreErrorKey.PARSE_ERROR,
        arguments: { formatterKey: this.key, options: this.options, value },
        message: `Parse value error: Value '${value}' invalid (${this.key})`
      });
    }
    switch (this.options.parseTo) {
      case 'iso':
        return momentValue.toISOString(true);
      case 'utc-iso':
        return momentValue.toISOString();
      case 'number':
        return momentValue.valueOf();
      case 'date':
        return momentValue.toDate();
      case 'utc-string':
        return momentValue.toDate().toUTCString();
      case 'utc-date':
        return momentValue.isUTC() ? momentValue.toDate() : moment.utc(momentValue).toDate();
    }
    throw new ApplicationError({
      key: CoreErrorKey.INVALID_STATE,
      message: `Invalid state`
    });
  }

  clone(options: DateFormatterOptions): DateFormatter {
    return new DateFormatter(Object.assign({}, this.options, options));
  }

  static setupOptions(options: DateFormatterOptions, key: string = CoreFormatterKey.DATE): DateFormatterOptions {
    try {
      return setupOptions({
        format: 'YYYY-MM-DD',
        nullFormat: '',
        parseTo: { default: 'iso', values: ['iso', 'number', 'date', 'utc-iso', 'utc-string', 'utc-date'] },
        offsetMode: { default: 'local', values: ['local', 'utc', 'zone'] },
        strict: false
      }, options);
    } catch (error) {
      throw new ApplicationError({
        key: CoreErrorKey.FORMATTER_OPTIONS_INVALID,
        arguments: { formatterKey: key, options, error },
        message: `Formatter options for '${key}' invalid`
      });
    }
  }

}

export function formatDate(value: number | Date, options?: DateFormatterOptions): string {
  const formatter = FormatterProvider.resolveFormatter(CoreFormatKey.DATE, options) || new DateFormatter(options);
  return formatter.format(value);
}

export function parseDate<T extends number | Date>(value: string, options?: DateFormatterOptions): T {
  const formatter = FormatterProvider.resolveFormatter(CoreFormatKey.DATE, options) || new DateFormatter(options);
  return formatter.parse(value) as T;
}
